import { fetchAuthSession, signInWithRedirect, signOut as amplifySignOut } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Me, UiLocale } from '@dubforge/shared';
import { api, ApiError } from '../api/client';
import { saveEmail, saveLocale } from '../api/hooks';
import { setLocale } from '../i18n';

/**
 * `error` keeps Cognito's raw text, e.g. "[not_invited] This Discord account…"; screens show it in the
 * visitor's language with parseAuthError.
 */
type AuthState =
  | { status: 'loading' }
  | { status: 'signedOut'; error?: string }
  | { status: 'signedIn'; me: Me };

interface AuthContextValue {
  state: AuthState;
  signIn: (returnTo?: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Changes the interface language, saving it to the account when signed in. */
  changeLocale: (locale: UiLocale) => Promise<void>;
  /** Saves the address notifications are emailed to. */
  changeEmail: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const RETURN_KEY = 'dubforge.returnTo';

export function takeReturnPath(): string {
  try {
    const path = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return path && path.startsWith('/') && !path.startsWith('/auth') ? path : '/';
  } catch {
    return '/';
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens?.idToken) {
        setState({ status: 'signedOut' });
        return;
      }
      const me = await api<Me>('GET', '/me');
      if (me.locale) await setLocale(me.locale);
      setState({ status: 'signedIn', me });
    } catch (err) {
      const error =
        err instanceof ApiError && err.code === 'access_off'
          ? '[access_off]'
          : err instanceof Error
            ? err.message
            : undefined;
      // Keep the message visible. Signing out here would redirect to Cognito's logout page; signIn
      // clears the rejected session instead if the user tries again.
      setState({ status: 'signedOut', error });
    }
  }, []);

  useEffect(() => {
    void refresh();
    return Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signInWithRedirect':
        case 'signedIn':
        case 'tokenRefresh':
          void refresh();
          break;
        case 'signedOut':
          setState({ status: 'signedOut' });
          break;
        case 'signInWithRedirect_failure':
        case 'tokenRefresh_failure': {
          const data = 'data' in payload ? (payload.data as { error?: Error } | undefined) : undefined;
          setState({
            status: 'signedOut',
            error: data?.error?.message ?? '[sign_in_failed]',
          });
          break;
        }
      }
    });
  }, [refresh]);

  const value = useMemo<AuthContextValue>(
    () => ({
      state,
      signIn: async (returnTo) => {
        try {
          if (returnTo) sessionStorage.setItem(RETURN_KEY, returnTo);
        } catch {
          // Storage unavailable: we'll land on the home page after sign-in.
        }
        try {
          await signInWithRedirect({ provider: { custom: 'Discord' } });
        } catch (err) {
          // A session the API rejected is still stored locally. Sign out (which redirects back to the
          // app) so the next attempt starts fresh.
          if ((err as Error).name === 'UserAlreadyAuthenticatedException') await amplifySignOut();
          else throw err;
        }
      },
      signOut: async () => {
        await amplifySignOut();
        setState({ status: 'signedOut' });
      },
      changeEmail: async (email) => {
        await saveEmail(email);
        if (state.status === 'signedIn') {
          setState({ status: 'signedIn', me: { ...state.me, email, emailSource: 'custom' } });
        }
      },
      changeLocale: async (locale) => {
        await setLocale(locale);
        if (state.status === 'signedIn') {
          await saveLocale(locale);
          setState({ status: 'signedIn', me: { ...state.me, locale } });
        }
      },
    }),
    [state],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/** The signed-in user. Only call inside routes guarded by RequireAuth. */
export function useMe(): Me {
  const { state } = useAuth();
  if (state.status !== 'signedIn') throw new Error('useMe called while signed out');
  return state.me;
}
